import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Save,
  Send,
  Calendar,
  Sparkles,
  Upload,
  Image as ImageIcon,
  Video,
  X,
  Plus,
  Check,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Wand2,
  Hash,
  AtSign,
  Link2,
  Smile,
  Eye,
  EyeOff,
  Settings,
  Loader2,
  AlertCircle,
  FileText,
  GripVertical,
  Trash2,
  Copy,
  MoreHorizontal,
  Monitor,
  Smartphone,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import LanguageSelector from '../../components/ui/LanguageSelector';
import {
  SocialPlatform,
  ContentFormat,
  ContentData,
  MediaFile,
  ContentTone,
  SchedulingOptions,
  PlatformContentOverride,
  PLATFORM_CONFIGS,
  CONTENT_TONES,
  validateContentForPlatform,
  getCharacterCountStatus
} from '../../types/contentHub';
import { contentHubService } from '../../services/contentHubService';
import { mediaService } from '../../services/mediaService';
import { useProject } from '../../contexts/ProjectContext';

// Platform Icon Component
const PlatformIcon: React.FC<{ platform: SocialPlatform; size?: number }> = ({ platform, size = 20 }) => {
  const config = PLATFORM_CONFIGS[platform];
  const icons: Record<SocialPlatform, React.ReactNode> = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    youtube_shorts: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
      </svg>
    ),
    tiktok: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    threads: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
      </svg>
    )
  };

  return <span style={{ color: config.color }}>{icons[platform]}</span>;
};

// Character Counter Component
const CharacterCounter: React.FC<{ text: string; platform: SocialPlatform }> = ({ text, platform }) => {
  const status = getCharacterCountStatus(text, platform);
  const colorClass = status.status === 'error' ? 'text-red-500' : status.status === 'warning' ? 'text-yellow-500' : 'text-gray-400';

  return (
    <span className={`text-xs ${colorClass}`}>
      {status.count}/{status.max}
    </span>
  );
};

// Platform Preview Component
const PlatformPreview: React.FC<{
  platform: SocialPlatform;
  content: ContentData;
  override?: PlatformContentOverride;
}> = ({ platform, content, override }) => {
  const { t } = useTranslation();
  const config = PLATFORM_CONFIGS[platform];
  const text = override?.text || content.text;
  const hashtags = override?.hashtags || content.hashtags;
  const media = override?.media || content.media;

  const renderPreview = () => {
    switch (platform) {
      case 'instagram':
        return (
          <div className="bg-white rounded-lg overflow-hidden max-w-[280px] sm:max-w-[320px]">
            {/* Header */}
            <div className="flex items-center gap-3 p-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-orange-500 rounded-full" />
              <span className="font-semibold text-sm">your_account</span>
            </div>
            {/* Media */}
            {media.length > 0 && (
              <div className="aspect-square bg-gray-100">
                {media[0].type === 'image' || media[0].type === 'gif' ? (
                  <img src={media[0].url || media[0].thumbnailUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
            )}
            {/* Content */}
            <div className="p-3">
              <p className="text-sm whitespace-pre-wrap">
                <span className="font-semibold">your_account</span>{' '}
                {text}
                {hashtags.length > 0 && (
                  <span className="text-blue-500"> #{hashtags.join(' #')}</span>
                )}
              </p>
            </div>
          </div>
        );

      case 'twitter':
        return (
          <div className="bg-white rounded-xl p-4 max-w-[280px] sm:max-w-[320px] border border-gray-200">
            <div className="flex gap-3">
              <div className="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-sm">Your Name</span>
                  <span className="text-gray-500 text-sm">@yourhandle</span>
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap">
                  {text}
                  {hashtags.length > 0 && (
                    <span className="text-blue-500"> #{hashtags.join(' #')}</span>
                  )}
                </p>
                {media.length > 0 && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
                    <img src={media[0].url || media[0].thumbnailUrl} alt="" className="w-full aspect-video object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'linkedin':
        return (
          <div className="bg-white rounded-lg overflow-hidden max-w-[280px] sm:max-w-[320px] border border-gray-200">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm">Your Name</p>
                  <p className="text-xs text-gray-500">Your Title • 1st</p>
                  <p className="text-xs text-gray-400">Just now • 🌐</p>
                </div>
              </div>
              <p className="text-sm mt-3 whitespace-pre-wrap">
                {text}
                {hashtags.length > 0 && (
                  <span className="text-blue-600"> #{hashtags.join(' #')}</span>
                )}
              </p>
            </div>
            {media.length > 0 && (
              <img src={media[0].url || media[0].thumbnailUrl} alt="" className="w-full aspect-video object-cover" />
            )}
          </div>
        );

      default:
        return (
          <div className="bg-white rounded-lg p-4 max-w-[280px] sm:max-w-[320px] border border-gray-200">
            <p className="text-sm whitespace-pre-wrap">{text}</p>
            {media.length > 0 && (
              <div className="mt-3 rounded-lg overflow-hidden">
                <img src={media[0].url || media[0].thumbnailUrl} alt="" className="w-full object-cover" />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <PlatformIcon platform={platform} size={18} />
        <span className="font-medium text-sm">{config.name}</span>
        <CharacterCounter text={text} platform={platform} />
      </div>
      <div className="bg-gray-100 rounded-lg p-4 flex justify-center">
        {renderPreview()}
      </div>
    </div>
  );
};

// Media Upload Component
const MediaUploader: React.FC<{
  media: MediaFile[];
  onMediaAdd: (files: File[]) => void;
  onMediaRemove: (mediaId: string) => void;
  onMediaReorder: (media: MediaFile[]) => void;
  maxFiles?: number;
  acceptedTypes?: string;
}> = ({ media, onMediaAdd, onMediaRemove, onMediaReorder, maxFiles = 10, acceptedTypes = 'image/*,video/*' }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    onMediaAdd(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onMediaAdd(files);
    }
  };

  return (
    <div className="space-y-4">
      {/* Uploaded media grid */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {media.map((file, index) => (
            <div key={file.id} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
              {file.type === 'video' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <Video className="w-8 h-8 text-white" />
                </div>
              ) : (
                <img src={file.url || file.thumbnailUrl} alt="" className="w-full h-full object-cover" />
              )}

              {/* Upload progress */}
              {file.status === 'uploading' && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" />
                    <span className="text-xs">{file.uploadProgress}%</span>
                  </div>
                </div>
              )}

              {/* Error state */}
              {file.status === 'error' && (
                <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              )}

              {/* Remove button */}
              <button
                onClick={() => onMediaRemove(file.id)}
                className="absolute top-1 right-1 p-1 bg-black/70 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Drag handle */}
              <div className="absolute top-1 left-1 p-1 bg-black/70 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                <GripVertical className="w-4 h-4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      {media.length < maxFiles && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('contentHub.creator.dropFiles', 'Drop files here or click to upload')}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {t('contentHub.creator.maxFiles', 'Max {{count}} files', { count: maxFiles })}
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

// Image Style Options
const IMAGE_STYLES = [
  { id: 'realistic', label: 'Realista' },
  { id: 'illustration', label: 'Ilustração' },
  { id: 'minimalist', label: 'Minimalista' },
  { id: '3d', label: '3D Render' },
  { id: 'creative', label: 'Criativo' }
];

// AI Generation Panel
const AIGenerationPanel: React.FC<{
  onGenerate: (result: {
    text: string;
    hashtags: string[];
    imageUrl?: string | null;
    platformContent?: Record<string, { text: string; charCount: number; charLimit: number }>;
  }) => void;
  platforms: SocialPlatform[];
}> = ({ onGenerate, platforms }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<ContentTone>('professional');
  const [targetAudience, setTargetAudience] = useState('');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [generateImage, setGenerateImage] = useState(true);
  const [imageStyle, setImageStyle] = useState<'realistic' | 'illustration' | 'minimalist' | '3d' | 'creative'>('realistic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setGenerationStatus(generateImage ? 'Gerando texto e imagem...' : 'Gerando texto...');

    try {
      const result = await contentHubService.generateFullPost({
        prompt,
        platforms,
        tone,
        targetAudience,
        includeHashtags,
        includeEmojis,
        generateImage,
        imageStyle
      });

      onGenerate({
        text: result.text,
        hashtags: result.hashtags,
        imageUrl: result.imageUrl,
        platformContent: result.platformContent
      });

      if (result.imageError) {
        alert(result.imageError);
      }
    } catch (error: any) {
      console.error('Error generating content:', error);
      alert(error.message || 'Falha ao gerar conteúdo. Tente novamente.');
    } finally {
      setIsGenerating(false);
      setGenerationStatus('');
    }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-indigo-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('contentHub.creator.aiGenerate', 'AI Content Generator')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('contentHub.creator.aiDescription', 'Generate text and image with AI')}</p>
          </div>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('contentHub.creator.aiPrompt', 'Describe what you want to create')}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('contentHub.creator.aiPromptPlaceholder', 'Ex: Post sobre dicas de produtividade para empreendedores, mostrando pessoa trabalhando em home office moderno...')}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              rows={4}
            />
          </div>

          {/* Tone and Audience */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('contentHub.creator.tone', 'Tone')}
              </label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as ContentTone)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
              >
                {CONTENT_TONES.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {t('contentHub.creator.targetAudience', 'Target Audience')}
              </label>
              <input
                type="text"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder={t('contentHub.creator.audiencePlaceholder', 'e.g., entrepreneurs, marketers')}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>

          {/* Image Generation Options */}
          <div className="p-3 bg-white/50 rounded-lg border border-indigo-100">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={generateImage}
                onChange={(e) => setGenerateImage(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                <ImageIcon className="w-4 h-4 inline mr-1" />
                Gerar imagem com IA (DALL-E)
              </span>
            </label>

            {generateImage && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Estilo da imagem
                </label>
                <div className="flex flex-wrap gap-2">
                  {IMAGE_STYLES.map(style => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={() => setImageStyle(style.id as any)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                        imageStyle === style.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-indigo-300'
                      }`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Text Options */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeHashtags}
                onChange={(e) => setIncludeHashtags(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('contentHub.creator.includeHashtags', 'Include hashtags')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeEmojis}
                onChange={(e) => setIncludeEmojis(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('contentHub.creator.includeEmojis', 'Include emojis')}</span>
            </label>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating || platforms.length === 0}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {generationStatus || t('contentHub.creator.generating', 'Generating...')}
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                {generateImage ? 'Gerar Texto + Imagem' : t('contentHub.creator.generateContent', 'Generate Content')}
              </>
            )}
          </Button>

          {generateImage && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              A geração de imagem pode levar alguns segundos
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Scheduling Modal
const SchedulingModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (options: SchedulingOptions) => void;
  scheduling: SchedulingOptions;
  onSchedulingChange: (options: SchedulingOptions) => void;
}> = ({ isOpen, onClose, onSchedule, scheduling, onSchedulingChange }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{t('contentHub.scheduling.title', 'Schedule Post')}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Date picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('contentHub.scheduling.date', 'Date')}
            </label>
            <input
              type="date"
              value={scheduling.scheduledDate ? scheduling.scheduledDate.toISOString().split('T')[0] : ''}
              onChange={(e) => onSchedulingChange({
                ...scheduling,
                scheduledDate: e.target.value ? new Date(e.target.value) : undefined
              })}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Time picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('contentHub.scheduling.time', 'Time')}
            </label>
            <input
              type="time"
              value={scheduling.scheduledTime || ''}
              onChange={(e) => onSchedulingChange({ ...scheduling, scheduledTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('contentHub.scheduling.timezone', 'Timezone')}
            </label>
            <select
              value={scheduling.timezone}
              onChange={(e) => onSchedulingChange({ ...scheduling, timezone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
            >
              <option value="America/Sao_Paulo">Brasilia (GMT-3)</option>
              <option value="America/New_York">New York (GMT-5)</option>
              <option value="America/Los_Angeles">Los Angeles (GMT-8)</option>
              <option value="Europe/London">London (GMT+0)</option>
              <option value="Europe/Paris">Paris (GMT+1)</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => onSchedule(scheduling)}
            disabled={!scheduling.scheduledDate}
            className="flex-1"
          >
            <Calendar className="w-4 h-4 mr-2" />
            {t('contentHub.scheduling.schedule', 'Schedule')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ContentCreatorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentProject } = useProject();

  const mode = searchParams.get('mode') as 'generate' | 'upload' || 'upload';

  // State
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);
  const [content, setContent] = useState<ContentData>({
    text: '',
    media: [],
    hashtags: [],
    mentions: []
  });
  const [platformOverrides, setPlatformOverrides] = useState<Partial<Record<SocialPlatform, PlatformContentOverride>>>({});
  const [scheduling, setScheduling] = useState<SchedulingOptions>({
    publishNow: true,
    timezone: 'America/Sao_Paulo',
    repeatEnabled: false
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [hashtagInput, setHashtagInput] = useState('');

  // Platform selection
  const togglePlatform = (platform: SocialPlatform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  // Content handlers
  const handleTextChange = (text: string) => {
    setContent(prev => ({ ...prev, text }));
  };

  const handleAddHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !content.hashtags.includes(tag)) {
      setContent(prev => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setContent(prev => ({ ...prev, hashtags: prev.hashtags.filter(h => h !== tag) }));
  };

  // Media handlers
  const handleMediaAdd = async (files: File[]) => {
    if (!currentProject?.id) return;

    for (const file of files) {
      const id = Math.random().toString(36).substring(7);
      const previewUrl = mediaService.createPreviewUrl(file);
      const mediaType = mediaService.getMediaType(file);

      // Add with uploading status
      setContent(prev => ({
        ...prev,
        media: [...prev.media, {
          id,
          type: mediaType,
          url: previewUrl,
          fileName: file.name,
          fileSize: file.size,
          status: 'uploading',
          uploadProgress: 0
        }]
      }));

      try {
        const uploaded = await mediaService.uploadFile(file, currentProject.id, {
          onProgress: (progress) => {
            setContent(prev => ({
              ...prev,
              media: prev.media.map(m =>
                m.id === id ? { ...m, uploadProgress: progress.percentage } : m
              )
            }));
          }
        });

        setContent(prev => ({
          ...prev,
          media: prev.media.map(m =>
            m.id === id ? { ...uploaded, id } : m
          )
        }));
      } catch (error) {
        console.error('Error uploading file:', error);
        setContent(prev => ({
          ...prev,
          media: prev.media.map(m =>
            m.id === id ? { ...m, status: 'error', errorMessage: 'Upload failed' } : m
          )
        }));
      }
    }
  };

  const handleMediaRemove = (mediaId: string) => {
    const mediaItem = content.media.find(m => m.id === mediaId);
    if (mediaItem?.url?.startsWith('blob:')) {
      mediaService.revokePreviewUrl(mediaItem.url);
    }
    setContent(prev => ({
      ...prev,
      media: prev.media.filter(m => m.id !== mediaId)
    }));
  };

  const handleMediaReorder = (media: MediaFile[]) => {
    setContent(prev => ({ ...prev, media }));
  };

  // AI generation handler
  const handleAIGenerate = (result: {
    text: string;
    hashtags: string[];
    imageUrl?: string | null;
    platformContent?: Record<string, { text: string; charCount: number; charLimit: number }>;
  }) => {
    // Update main content
    setContent(prev => {
      const newContent = {
        ...prev,
        text: result.text,
        hashtags: [...new Set([...prev.hashtags, ...result.hashtags])]
      };

      // If an image was generated, add it to the media array
      if (result.imageUrl) {
        const newMediaItem: MediaFile = {
          id: `ai_${Date.now()}`,
          type: 'image',
          url: result.imageUrl,
          fileName: 'ai-generated-image.png',
          fileSize: 0,
          status: 'ready'
        };
        newContent.media = [...prev.media, newMediaItem];
      }

      return newContent;
    });

    // Fill platform-specific overrides if provided
    if (result.platformContent) {
      const newOverrides: Partial<Record<SocialPlatform, PlatformContentOverride>> = {};

      Object.entries(result.platformContent).forEach(([platform, data]) => {
        newOverrides[platform as SocialPlatform] = {
          text: data.text,
          hashtags: result.hashtags
        };
      });

      setPlatformOverrides(prev => ({ ...prev, ...newOverrides }));
    }
  };

  // Save handlers
  const handleSave = async () => {
    if (selectedPlatforms.length === 0) return;

    setIsSaving(true);
    try {
      const format: ContentFormat = content.media.some(m => m.type === 'video')
        ? 'video'
        : content.media.length > 1
          ? 'carousel'
          : 'post';

      await contentHubService.createContent({
        content,
        platforms: selectedPlatforms,
        format
      });
      navigate('/content');
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (selectedPlatforms.length === 0) return;

    setIsPublishing(true);
    try {
      const format: ContentFormat = content.media.some(m => m.type === 'video')
        ? 'video'
        : content.media.length > 1
          ? 'carousel'
          : 'post';

      // First create the content
      const created = await contentHubService.createContent({
        content,
        platforms: selectedPlatforms,
        format
      });

      // Then publish
      await contentHubService.publishNow(created.id);

      navigate('/content');
    } catch (error) {
      console.error('Error publishing content:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSchedule = async (options: SchedulingOptions) => {
    if (selectedPlatforms.length === 0) return;

    setShowScheduleModal(false);
    setIsSaving(true);
    try {
      const format: ContentFormat = content.media.some(m => m.type === 'video')
        ? 'video'
        : content.media.length > 1
          ? 'carousel'
          : 'post';

      const created = await contentHubService.createContent({
        content,
        platforms: selectedPlatforms,
        format,
        scheduledAt: options.scheduledDate
      });

      // Schedule the content
      await contentHubService.scheduleContent(created.id, options);

      navigate('/content');
    } catch (error) {
      console.error('Error scheduling content:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Validation
  const validationErrors = selectedPlatforms.flatMap(platform => {
    const result = validateContentForPlatform(content, platform);
    return result.errors;
  });

  const canPublish = selectedPlatforms.length > 0 && content.text.trim() && validationErrors.length === 0;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-between gap-2 px-2 sm:px-4 flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/content')}
            aria-label={t('common.back', 'Voltar')}
            className="text-gray-400 hover:text-white hover:bg-gray-800 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="hidden sm:block h-6 w-px bg-gray-700" />
          <h1 className="text-white font-medium text-sm sm:text-base truncate min-w-0">
            {mode === 'generate'
              ? t('contentHub.creator.titleGenerate', 'Create with AI')
              : t('contentHub.creator.titleUpload', 'Create Content')
            }
          </h1>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            aria-label={showPreview ? t('common.hidePreview', 'Hide Preview') : t('common.showPreview', 'Show Preview')}
            title={showPreview ? t('common.hidePreview', 'Hide Preview') : t('common.showPreview', 'Show Preview')}
            className="text-gray-400 hover:text-white hover:bg-gray-800 whitespace-nowrap"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span className="hidden lg:inline ml-2">{showPreview ? t('common.hidePreview', 'Hide Preview') : t('common.showPreview', 'Show Preview')}</span>
          </Button>

          <div className="hidden md:block">
            <LanguageSelector />
          </div>

          <div className="hidden sm:block h-6 w-px bg-gray-700" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || selectedPlatforms.length === 0}
            aria-label={t('common.saveDraft', 'Save Draft')}
            title={t('common.saveDraft', 'Save Draft')}
            className="!bg-gray-700 !text-white hover:!bg-gray-600 border border-gray-600 whitespace-nowrap"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="hidden sm:inline ml-2">{t('common.saveDraft', 'Save Draft')}</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowScheduleModal(true)}
            disabled={!canPublish}
            aria-label={t('contentHub.creator.schedule', 'Schedule')}
            title={t('contentHub.creator.schedule', 'Schedule')}
            className="text-gray-300 hover:text-white hover:bg-gray-800 whitespace-nowrap"
          >
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline ml-2">{t('contentHub.creator.schedule', 'Schedule')}</span>
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            disabled={isPublishing || !canPublish}
            aria-label={t('contentHub.creator.publishNow', 'Publish Now')}
            className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
          >
            {isPublishing ? (
              <Loader2 className="w-4 h-4 mr-1.5 sm:mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1.5 sm:mr-2" />
            )}
            <span className="sm:hidden">{t('contentHub.creator.publishShort', 'Publicar')}</span>
            <span className="hidden sm:inline">{t('contentHub.creator.publishNow', 'Publish Now')}</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Panel - Editor */}
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="max-w-2xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
            {/* Platform Selector */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
                {t('contentHub.creator.selectPlatforms', 'Select Platforms')}
              </h2>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                {Object.values(PLATFORM_CONFIGS).map(config => (
                  <button
                    key={config.id}
                    onClick={() => togglePlatform(config.id)}
                    className={`min-w-0 flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg border-2 transition-all ${
                      selectedPlatforms.includes(config.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <PlatformIcon platform={config.id} size={20} />
                    <span className="font-medium text-sm truncate min-w-0 flex-1 text-left dark:text-gray-200">{config.name}</span>
                    {selectedPlatforms.includes(config.id) && (
                      <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* AI Generation (only in generate mode) */}
            {mode === 'generate' && (
              <AIGenerationPanel
                onGenerate={handleAIGenerate}
                platforms={selectedPlatforms}
              />
            )}

            {/* Content Editor */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
                {t('contentHub.creator.content', 'Content')}
              </h2>

              {/* Text Editor */}
              <div className="mb-4">
                <textarea
                  value={content.text}
                  onChange={(e) => handleTextChange(e.target.value)}
                  placeholder={t('contentHub.creator.textPlaceholder', 'Write your content here...')}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-h-[100px] sm:min-h-[150px] dark:bg-gray-800 dark:text-white"
                />
                {selectedPlatforms.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                    {selectedPlatforms.map(platform => (
                      <div key={platform} className="flex items-center gap-1">
                        <PlatformIcon platform={platform} size={14} />
                        <CharacterCounter text={content.text} platform={platform} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hashtags */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Hash className="w-4 h-4 inline mr-1" />
                  {t('contentHub.creator.hashtags', 'Hashtags')}
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {content.hashtags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm"
                    >
                      #{tag}
                      <button
                        onClick={() => handleRemoveHashtag(tag)}
                        className="hover:text-indigo-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHashtag())}
                    placeholder={t('contentHub.creator.addHashtag', 'Add hashtag...')}
                    className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm dark:bg-gray-800 dark:text-white"
                  />
                  <Button variant="secondary" onClick={handleAddHashtag}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Media Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <ImageIcon className="w-4 h-4 inline mr-1" />
                  {t('contentHub.creator.media', 'Media')}
                </label>
                <MediaUploader
                  media={content.media}
                  onMediaAdd={handleMediaAdd}
                  onMediaRemove={handleMediaRemove}
                  onMediaReorder={handleMediaReorder}
                />
              </div>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">{t('contentHub.creator.validationErrors', 'Validation Errors')}</span>
                </div>
                <ul className="text-sm text-red-600 space-y-1">
                  {validationErrors.map((error, i) => (
                    <li key={i}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Preview */}
        {showPreview && selectedPlatforms.length > 0 && (
          <div className="w-full min-w-0 lg:w-[400px] bg-gray-100 dark:bg-gray-900 lg:border-l border-t lg:border-t-0 border-gray-200 dark:border-gray-700 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {t('contentHub.creator.preview', 'Preview')}
                </h2>
                <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`p-1.5 rounded ${previewDevice === 'mobile' ? 'bg-gray-100' : ''}`}
                  >
                    <Smartphone className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`p-1.5 rounded ${previewDevice === 'desktop' ? 'bg-gray-100' : ''}`}
                  >
                    <Monitor className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {selectedPlatforms.map(platform => (
                  <PlatformPreview
                    key={platform}
                    platform={platform}
                    content={content}
                    override={platformOverrides[platform]}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scheduling Modal */}
      <SchedulingModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleSchedule}
        scheduling={scheduling}
        onSchedulingChange={setScheduling}
      />
    </div>
  );
};

export default ContentCreatorPage;
